import React, { useState, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";

interface TooltipProps {
  children: React.ReactNode;
  /** Texto plano. Se escapa al pintarlo: no admite HTML a propósito. */
  content: string;
  position?: "top" | "bottom" | "left" | "right";
  delay?: number;
  disabled?: boolean;
  className?: string;
  fitContent?: boolean;
}

export default function Tooltip({
  children,
  content,
  position = "top",
  delay = 0,
  disabled = false,
  className,
  fitContent = false,
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const GAP = 8;

  const updateCoords = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let top = 0;
    let left = 0;
    switch (position) {
      case "top":
        top = rect.top - GAP;
        left = rect.left + rect.width / 2;
        break;
      case "bottom":
        top = rect.bottom + GAP;
        left = rect.left + rect.width / 2;
        break;
      case "left":
        top = rect.top + rect.height / 2;
        left = rect.left - GAP;
        break;
      case "right":
        top = rect.top + rect.height / 2;
        left = rect.right + GAP;
        break;
    }
    setCoords({ top, left });
  }, [position]);

  const handleMouseEnter = () => {
    if (disabled) return;
    if (delay === 0) {
      updateCoords();
      setIsVisible(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        updateCoords();
        setIsVisible(true);
      }, delay);
    }
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(false);
  };

  // Reposicionar mientras esté visible (scroll/resize) para que siga al elemento
  useLayoutEffect(() => {
    if (!isVisible) return;
    updateCoords();
    const handler = () => updateCoords();
    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [isVisible, updateCoords]);

  const getTransform = () => {
    switch (position) {
      case "top":
        return "translate(-50%, -100%)";
      case "bottom":
        return "translate(-50%, 0)";
      case "left":
        return "translate(-100%, -50%)";
      case "right":
        return "translate(0, -50%)";
      default:
        return "translate(-50%, -100%)";
    }
  };

  const getArrowClasses = () => {
    switch (position) {
      case "top":
        return "top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-content";
      case "bottom":
        return "bottom-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-content";
      case "left":
        return "left-full top-1/2 -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-content";
      case "right":
        return "right-full top-1/2 -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-content";
      default:
        return "top-full left-1/2 -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-content";
    }
  };

  return (
    <div
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`${className ?? ""} relative inline-block min-w-0 ${
        fitContent ? "w-auto" : "w-full"
      }`}
    >
      {children}

      {isVisible &&
        !disabled &&
        createPortal(
          <div
            className="fixed z-[9999] pointer-events-none"
            style={{
              top: coords.top,
              left: coords.left,
              transform: getTransform(),
            }}
          >
            <div className="relative px-2 py-1 bg-content text-app text-[10px] font-medium rounded shadow-lg text-center whitespace-nowrap">
              {/*
                Texto plano, nunca HTML. Esto era `dangerouslySetInnerHTML` y
                ninguno de los llamadores lo necesitaba: los veinticuatro pasan
                una cadena de i18n y ninguna de esas traducciones lleva
                etiquetas. Lo que sí dejaba abierto es que el día que alguien
                pase aquí el nombre de una unidad, de un conductor o una nota de
                cliente —todos editables desde la plataforma— se convierta en
                XSS almacenado sin que nada en el tipo lo advierta.
              */}
              <div>{content}</div>
              {/* Flecha */}
              <div className={`absolute w-0 h-0 ${getArrowClasses()}`} />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
